import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, Download, Calendar, TrendingUp, TrendingDown,
  PieChart, Target, ChevronLeft, ChevronRight, FileText, Printer
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { useLanguage } from '../context/LanguageContext';
import { formatCurrency } from '../lib/utils';
import { toast } from 'sonner';
import api from '../lib/api';

const CATEGORY_COLORS = {
  logement: '#FF4F00',
  transport: '#3B82F6',
  alimentation: '#10B981',
  abonnements: '#8B5CF6',
  shopping: '#EC4899',
  loisirs: '#F59E0B',
  sante: '#EF4444',
  epargne: '#00D68F',
  divers: '#A8A29E',
};

const CATEGORY_NAMES_FR = {
  logement: 'Logement',
  transport: 'Transport',
  alimentation: 'Alimentation',
  abonnements: 'Abonnements',
  shopping: 'Shopping',
  loisirs: 'Loisirs',
  sante: 'Santé',
  epargne: 'Épargne',
  divers: 'Divers',
};

export default function ReportsPage() {
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  useEffect(() => {
    loadReport();
  }, [selectedMonth, selectedYear]);

  const loadReport = async () => {
    try {
      setLoading(true);
      const res = await api.get('/reports/monthly', {
        params: { month: selectedMonth, year: selectedYear }
      });
      setReport(res.data);
    } catch (error) {
      toast.error(t('loading_error'));
    } finally {
      setLoading(false);
    }
  };

  const handlePreviousMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const handleNextMonth = () => {
    const now = new Date();
    const currentMonth = now.getMonth() + 1;
    const currentYear = now.getFullYear();
    
    // Don't go beyond current month
    if (selectedYear === currentYear && selectedMonth >= currentMonth) {
      return;
    }
    
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  const handleDownloadPDF = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/reports/monthly/html?month=${selectedMonth}&year=${selectedYear}`;
    window.open(url, '_blank');
    toast.success(language === 'fr' ? 'Rapport ouvert - utilisez Ctrl+P pour imprimer en PDF' : language === 'pt' ? 'Relatório aberto - use Ctrl+P para imprimir como PDF' : 'Report opened - use Ctrl+P to print as PDF');
  };

  const isCurrentMonth = () => {
    const now = new Date();
    return selectedMonth === now.getMonth() + 1 && selectedYear === now.getFullYear();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24 md:pb-8">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="font-bold text-lg">
                  {language === 'fr' ? 'Rapport Mensuel' : language === 'pt' ? 'Relatório Mensal' : 'Monthly Report'}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {language === 'fr' ? 'Analyse détaillée de vos finances' : language === 'pt' ? 'Análise detalhada das suas finanças' : 'Detailed financial analysis'}
                </p>
              </div>
            </div>
            <Button onClick={handleDownloadPDF}>
              <Printer className="w-4 h-4 mr-2" />
              PDF
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Month Selector */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <Button variant="ghost" size="icon" onClick={handlePreviousMonth}>
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <div className="text-center">
                  <p className="text-2xl font-bold">
                    {(() => {
                      const monthNum = report?.period?.month;
                      const year = report?.period?.year;
                      if (!monthNum || !year) return '';
                      const locale = language === 'pt' ? 'pt-PT' : language === 'en' ? 'en-US' : 'fr-FR';
                      const date = new Date(year, monthNum - 1, 1);
                      const monthName = date.toLocaleDateString(locale, { month: 'long' });
                      return `${monthName.charAt(0).toUpperCase() + monthName.slice(1)} ${year}`;
                    })()}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {report?.period?.start_date} → {report?.period?.end_date}
                  </p>
                </div>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={handleNextMonth}
                  disabled={isCurrentMonth()}
                >
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Summary Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4"
        >
          <Card className="bg-gradient-to-br from-primary/10 to-orange-100 dark:from-primary/20 dark:to-orange-900/20">
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">
                {t('total_spent')}
              </p>
              <p className="text-2xl font-bold text-primary">
                {formatCurrency(report?.summary?.total_spending || 0)}
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">
                {t('transactions')}
              </p>
              <p className="text-2xl font-bold">
                {report?.summary?.transaction_count || 0}
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">
                {t('avg_per_day')}
              </p>
              <p className="text-2xl font-bold">
                {formatCurrency(report?.summary?.daily_average || 0)}
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">
                {t('top_category')}
              </p>
              <p className="text-lg font-bold truncate">
                {t(`cat_${report?.summary?.top_category}`) || report?.summary?.top_category || '-'}
              </p>
            </CardContent>
          </Card>
        </motion.div>

        {/* Category Breakdown */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <PieChart className="w-5 h-5 text-primary" />
                {t('breakdown_by_category')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {Object.entries(report?.by_category || {}).map(([category, data]) => (
                <div key={category} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: CATEGORY_COLORS[category] || '#A8A29E' }}
                      />
                      <span className="font-medium">
                        {t(`cat_${category}`) || category}
                      </span>
                    </div>
                    <div className="text-right">
                      <span className="font-bold">{formatCurrency(data.total)}</span>
                      <span className="text-sm text-muted-foreground ml-2">
                        ({data.percentage}%)
                      </span>
                    </div>
                  </div>
                  <Progress 
                    value={data.percentage} 
                    className="h-2"
                    style={{ 
                      '--progress-background': CATEGORY_COLORS[category] || '#A8A29E'
                    }}
                  />
                </div>
              ))}
              
              {Object.keys(report?.by_category || {}).length === 0 && (
                <p className="text-center text-muted-foreground py-4">
                  {language === 'fr' ? 'Aucune transaction ce mois' : language === 'pt' ? 'Sem transações este mês' : 'No transactions this month'}
                </p>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Budget Status */}
        {report?.budget_status?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Target className="w-5 h-5 text-primary" />
                  {t('budget_status')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {report.budget_status.map((budget) => {
                  const isExceeded = budget.percentage > 100;
                  const isWarning = budget.percentage > 80;
                  
                  return (
                    <div key={budget.category} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">
                          {t(`cat_${budget.category}`) || budget.category}
                        </span>
                        <div className="text-right">
                          <span className={`font-bold ${isExceeded ? 'text-red-500' : ''}`}>
                            {formatCurrency(budget.spent)}
                          </span>
                          <span className="text-muted-foreground"> / {formatCurrency(budget.budget)}</span>
                        </div>
                      </div>
                      <div className="relative h-2 bg-muted rounded-full overflow-hidden">
                        <div 
                          className={`absolute h-full rounded-full transition-all ${
                            isExceeded ? 'bg-red-500' : isWarning ? 'bg-amber-500' : 'bg-green-500'
                          }`}
                          style={{ width: `${Math.min(budget.percentage, 100)}%` }}
                        />
                      </div>
                      {isExceeded && (
                        <p className="text-xs text-red-500">
                          ⚠️ {language === 'fr' ? 'Budget dépassé de' : language === 'pt' ? 'Orçamento excedido em' : 'Over budget by'} {formatCurrency(budget.spent - budget.budget)}
                        </p>
                      )}
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Objectives Progress */}
        {report?.objective_progress?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-500" />
                  {language === 'fr' ? 'Progression des objectifs' : language === 'pt' ? 'Progresso dos objetivos' : 'Objectives progress'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {report.objective_progress.map((obj, idx) => (
                  <div key={idx} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{obj.name}</span>
                      <div className="text-right">
                        <span className="font-bold text-green-600">
                          {formatCurrency(obj.current)}
                        </span>
                        <span className="text-muted-foreground"> / {formatCurrency(obj.target)}</span>
                      </div>
                    </div>
                    <div className="relative h-2 bg-muted rounded-full overflow-hidden">
                      <div 
                        className="absolute h-full bg-green-500 rounded-full transition-all"
                        style={{ width: `${Math.min(obj.percentage, 100)}%` }}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground text-right">
                      {obj.percentage}% {t('reached')}
                    </p>
                  </div>
                ))}
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Tips */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <Card className="bg-blue-50 dark:bg-blue-950/20 border-blue-200">
            <CardContent className="p-4">
              <p className="text-sm text-blue-700 dark:text-blue-300">
                {language === 'fr' 
                  ? '💡 Conseil : Téléchargez ce rapport en PDF pour le conserver ou le partager avec votre conseiller financier.'
                  : language === 'pt'
                  ? '💡 Dica: Transfira este relatório em PDF para o guardar ou partilhar com o seu consultor financeiro.'
                  : '💡 Tip: Download this report as PDF to keep it or share with your financial advisor.'}
              </p>
            </CardContent>
          </Card>
        </motion.div>
      </main>
    </div>
  );
}
